import { useState, useEffect, useCallback } from 'react';
import { ColumnConfig, DEFAULT_COLUMNS } from '../types';

const STORAGE_KEY = 'karting-timing-columns';

export const useColumnSettings = () => {
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedColumns = JSON.parse(stored) as ColumnConfig[];
        
        // Merge with defaults to handle new columns that might have been added
        const mergedColumns = DEFAULT_COLUMNS.map(defaultCol => {
          const storedCol = parsedColumns.find(col => col.key === defaultCol.key);
          return storedCol ? { ...defaultCol, visible: storedCol.visible } : defaultCol;
        });
        
        setColumns(mergedColumns);
      }
    } catch (error) {
      console.error('Error loading column settings from localStorage:', error);
      setColumns(DEFAULT_COLUMNS);
    }
  }, []);

  // Save to localStorage whenever columns change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
    } catch (error) {
      console.error('Error saving column settings to localStorage:', error);
    }
  }, [columns]);

  const toggleColumn = useCallback((columnKey: string) => {
    setColumns(prev => 
      prev.map(col => 
        col.key === columnKey && !col.essential
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  }, []);

  const resetColumns = useCallback(() => {
    setColumns(DEFAULT_COLUMNS);
  }, []);

  const getVisibleColumns = useCallback(() => {
    return columns.filter(col => col.visible);
  }, [columns]);

  const showAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, []);

  const hideAllOptionalColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ 
      ...col, 
      visible: col.essential || false 
    })));
  }, []);

  return {
    columns,
    toggleColumn,
    resetColumns,
    getVisibleColumns,
    showAllColumns,
    hideAllOptionalColumns
  };
}; 